'use client';

import { useRef, useMemo, useEffect, useReducer } from 'react';
import { useRouter, usePathname } from 'src/navigation';
import { useSearchParams } from 'next/navigation';
import { routeForDocument } from '@metmuseum/routes';
import TopPanel from '../top-panel';
import Viewer from '../viewer';
import BottomPanel from '../bottom-panel';
import MobileInfoPanel from '../mobile-info-panel';
import AllSpreadsOverlay from '../all-spreads-overlay';
import useMediaQuery from 'hooks/use-media-query';
import { BREAKPOINT_M } from 'lib/constants.mjs';
import { createInitialViewerState, viewerReducer } from '../../state';
import styles from './digitized-primary-source-viewer.module.scss';
import type { DigitizedPrimarySourcePage } from '../../types';
import type { PortableTextBlock } from '@portabletext/types';

export default function DigitizedPrimarySourceViewer({
	title,
	researchProjectSlug,
	about,
	howToCite,
	pages,
}: DigitizedPrimarySourceViewerProps) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	// The ?page= query param is displayed as a 1-based index to be more user-friendly.
	const queryParamPageIndex = getInternalPageIndex(
		searchParams.get('page') ?? '1',
		pages.length,
	);

	const [state, dispatch] = useReducer(
		viewerReducer,
		createInitialViewerState({
			pageIndex: queryParamPageIndex,
			sources: pages,
		}),
	);
	const {
		pageIndex,
		isLoading,
		hasError,
		canZoomIn,
		canZoomOut,
		isFirstPage,
		isLastPage,
		showTranscription,
		showTranslation,
		showAllSpreads,
		showComments,
		activeCommentId,
		showAboutPanel,
		sources,
		isMobile,
	} = state;
	const currentPage = pages[pageIndex];
	const showMobileInfoPanel =
		isMobile &&
		(showTranscription ||
			showTranslation ||
			showAboutPanel ||
			!!activeCommentId);

	const thumbnails = useMemo(
		() =>
			sources.map(({ title, thumbnailUrl, id }) => ({
				title,
				url: thumbnailUrl,
				id,
			})),
		[sources],
	);
	const commentsMapById = useMemo(
		() =>
			sources.reduce((acc, { interpretations }) => {
				for (const interpretation of interpretations ?? []) {
					const { x, y, content, citation, id } = interpretation;
					acc[id] = { x, y, content, citation };
				}

				return acc;
			}, {}),
		[sources],
	);

	const viewerApiRef = useRef(null);
	const zoomIn = () => viewerApiRef.current?.zoomIn();
	const zoomOut = () => viewerApiRef.current?.zoomOut();

	useEffect(() => {
		router.replace(`${pathname}?page=${pageIndex + 1}`);
	}, [pageIndex, pathname, router]);

	const { matches } = useMediaQuery(`(max-width: ${BREAKPOINT_M}px)`);
	useEffect(() => {
		dispatch({ type: 'set_is_mobile', isMobile: matches });
	}, [matches]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (showAllSpreads) {
				return;
			}

			switch (e.key) {
				case 'ArrowLeft':
					dispatch({ type: 'go_to_previous_page' });
					break;
				case 'ArrowRight':
					dispatch({ type: 'go_to_next_page' });
					break;
				default:
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [showAllSpreads]);

	return (
		<div className={styles.container}>
			<TopPanel
				about={about}
				howToCite={howToCite}
				title={title}
				loading={isLoading}
				showAllSpreads={showAllSpreads}
				navigateToAboutPage={() =>
					router.push(
						routeForDocument({
							_type: 'researchProject',
							slug: researchProjectSlug,
						}),
					)
				}
				showComments={showComments}
				dispatch={dispatch}
				isMobile={isMobile}
			/>
			<div className={styles.viewerArea}>
				<div
					className={[
						styles.viewerContainer,
						// This removes the elements in the background from the accessibility tree
						// when the all spreads overlay is shown.
						showAllSpreads ? styles.hidden : '',
					].join(' ')}
				>
					<Viewer
						currentPageIndex={pageIndex}
						transcription={currentPage.transcription}
						translation={currentPage.englishTranslation}
						showTranscription={showTranscription}
						showTranslation={showTranslation}
						showMobileInfoPanel={showMobileInfoPanel}
						dispatch={dispatch}
						isLoading={isLoading}
						hasError={hasError}
						ref={viewerApiRef}
						sources={pages}
						comments={commentsMapById}
						showComments={showComments && !showMobileInfoPanel}
						isMobile={isMobile}
						activeCommentId={activeCommentId}
					/>
					<BottomPanel
						title={currentPage.title}
						isFirst={isFirstPage}
						isLast={isLastPage}
						canZoomIn={canZoomIn}
						canZoomOut={canZoomOut}
						zoomIn={zoomIn}
						zoomOut={zoomOut}
						showTranscription={showTranscription}
						showTranslation={showTranslation}
						dispatch={dispatch}
						hasError={hasError}
					/>
					{showMobileInfoPanel && (
						<MobileInfoPanel
							showTranscription={showTranscription}
							showTranslation={showTranslation}
							transcription={currentPage.transcription}
							translation={currentPage.englishTranslation}
							dispatch={dispatch}
							activeComment={commentsMapById[activeCommentId]}
							pageIndex={pageIndex}
							showAboutPanel={showAboutPanel}
							about={about}
							howToCite={howToCite}
						/>
					)}
				</div>
				<AllSpreadsOverlay
					thumbnails={thumbnails}
					currentPageIndex={pageIndex}
					dispatch={dispatch}
					hidden={!showAllSpreads}
				/>
			</div>
		</div>
	);
}

// Returns a 0-based index number value from a 1-based index string value.
function getInternalPageIndex(queryParamValue: string, maxIndex: number) {
	let value = Number.parseInt(queryParamValue);

	if (Number.isNaN(value) || value < 1) {
		value = 1;
	}

	if (value > maxIndex) {
		value = maxIndex;
	}

	return value - 1;
}

export interface DigitizedPrimarySourceViewerProps {
	title: string;
	researchProjectSlug?: { current: string };
	about: PortableTextBlock[];
	howToCite: string;
	interpretationCitation?: PortableTextBlock[];
	pages: DigitizedPrimarySourcePage[];
}
